import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, RouteComponentProps } from "react-router-dom";

import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Hidden from '@material-ui/core/Hidden';
import Skeleton from '@material-ui/lab/Skeleton';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { DateTime } from "luxon";

import { TrainingService } from "../../services";
import { TrainingCategories, TypeTraining } from "../../services/models";
import { useAuth } from "../../contexts/auth";
import { formatError, snackbarUtils } from "../../utils";

import Head from '../../components/Head';
import Layout from "../../layout";
import TrainingCard from "../Trainings/Card";
import useStyles from './styles';

type MatchParams = {
  id: string;
}
interface Props extends RouteComponentProps<MatchParams> { }

const ReadTraining = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
  const { user } = useAuth();

  const [loading, setLoading] = useState({
    suggestions: true,
    training: true,
  });
  const [training, setTraining] = useState<TypeTraining>({} as TypeTraining);
  const [trainings, setTrainings] = useState<TypeTraining[]>([]);
  const [categories, setCategories] = useState<TrainingCategories[]>([]);

  const luxonDate = DateTime.fromISO(training.createdDate).setLocale('pt-BR').toLocaleString();
  const categoriesIsEmpty = categories.length === 0;
  const trainingsIsEmpty = trainings.length === 0;

  useEffect(() => {
    async function getTraining() {
      setLoading(value => ({ ...value, training: true }));
      try {
        const { data } = await TrainingService.show(Number(props.match.params.id));
        await TrainingService.setViewedTraining(data.id);
        setTraining(data);
      } catch (error) {
        console.error('treinamento erro: ', error.message);
        snackbarUtils.error(formatError(error));
      } finally {
        setLoading(value => ({ ...value, training: false }));
      }
    }
    getTraining();
  }, [user.id, props.match.params.id]);

  useEffect(() => {
    async function getTrainings() {
      setLoading(value => ({ ...value, suggestions: true }));
      try {
        const { data } = await TrainingService.getTrainingByCategory();
        const formattedCategories = data.map((tran: any) => [0, 1].reduce((acc: any) => {
          acc.id = tran.id;
          acc.description = tran.description;
          return acc;
        }, {}));
        setCategories(formattedCategories);
        const filteredTrainings = data.find(tran => tran.description === training.category.description)
          ?.training.filter(tran => tran.id !== training.id);
        if (!!filteredTrainings) setTrainings(filteredTrainings.reverse());
      } catch (error) {
        console.error('treinamentos erro: ', error)
        snackbarUtils.error(formatError(error));
      } finally {
        setLoading(value => ({ ...value, suggestions: false }));
      }
    }
    if (!loading.training) getTrainings()
  }, [loading.training, training])

  const renderCategories = useCallback(() => {
    if (loading.suggestions && categoriesIsEmpty) return (
      Array.from(Array(2)).map((i, index) => (
        <Box key={`categories-skeletons-${index}`} width="100%" marginY={0.5}>
          <Skeleton variant="text" width="60%" />
        </Box>
      ))
    )
    return (
      !categoriesIsEmpty ? (
        categories.map(category => (
          <Link
            key={category.id}
            component={RouterLink}
            to={`/treinamentos?category=${category.description}`}
          >
            {category.description}
          </Link>
        ))
      ) : (
        <Typography>Sem Categorias.</Typography>
      )
    )
  }, [categories, categoriesIsEmpty, loading.suggestions]);

  const renderSuggestions = useCallback(() => {
    if (loading.suggestions && trainingsIsEmpty) return (
      Array.from(Array(2)).map((i, index) => (
        <Box key={`suggestions-skeletons-${index}`} width="100%">
          <Skeleton variant="rect" width="100%" height={160} style={{ borderRadius: 16 }} />
        </Box>
      ))
    )
    return (
      !trainingsIsEmpty ? (
        trainings.slice(0, 2).map(training => (
          <TrainingCard
            key={training.id}
            training={training}
            className={classes.trainingCard}
          />
        ))
      ) : (
        <Typography>Sem sugestões para você.</Typography>
      )
    )
  }, [classes.trainingCard, loading.suggestions, trainings, trainingsIsEmpty]);

  return (
    <Layout
      page="training"
      backLink="/treinamentos"
      innerPage
    >
      <Head>{training.title}</Head>
      <Container component="section" >
        <Grid container item xs={12} spacing={2} className={classes.container}>
          <Grid item xs={12} md={8} component="article">
            <Hidden only='xs'>
              <Typography variant="h2">
                {!loading.training ? training.title : <Skeleton />}
              </Typography>
            </Hidden>

            <Box className={classes.imageWrapper}>
              {!loading.training ? (
                <Box component="figure" className={classes.imageContainer}>
                  <Box id="training-data-container">
                    <Typography id="training-date">{!isMobile ? luxonDate : `Publicado em ${luxonDate}`}</Typography>
                    <Typography id="training-category">{training.category?.description}</Typography>
                  </Box>
                  <img src={training.image} alt={training.description} />
                </Box>
              ) : (
                <Skeleton variant="rect" width="100%" height={265} />
              )}
            </Box>

            <Hidden smUp>
              <Typography variant="h2">{training.title}</Typography>
            </Hidden>
            <Box className={classes.contentContainer}>
              <Typography id="training-description">
                {!loading.training ? training.description : <Skeleton height={45} />}
              </Typography>
              <Typography>
                {!loading.training ? training.text : <Skeleton height={300} />}
              </Typography>
            </Box>
          </Grid>

          <Hidden only="xs">
            <Grid item xs={4} component="aside" className={classes.aside}>
              <Box component='section'>
                <Box component='section' id="aside-categories-container">
                  <Typography variant="h3">Categorias</Typography>
                  {renderCategories()}
                </Box>
                <Box component='section' className={classes.suggestionsWrapper}>
                  <Typography variant="h3">Sugestão de Leitura</Typography>
                  <Box>
                    {renderSuggestions()}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Hidden>
        </Grid>
      </Container>
    </Layout>
  );
}

export default ReadTraining;